// Live mirror of the trail timings the transport captures while a stream is
// in flight. Shared between the in-flight trail and post-finalize persistence.
// Mid-stream refresh still loses the trail (no message persisted until finalize).
import { useSyncExternalStore } from 'react';

/** Loose shape — `completedAt` is optional while a step is still in flight. */
export interface LiveStepTiming {
  startedAt: number;
  completedAt?: number;
  gapBeforeMs?: number;
}

export type LiveStepTimingsMap = Record<string, LiveStepTiming>;

export interface LiveTrailTimingsEntry {
  requestStartedAt: number;
  stepTimings: LiveStepTimingsMap;
  /** Stamped on the running → finished transition; bridges until `stream_duration_ms` lands. */
  finishedAt?: number;
}

const ENTRY_TTL_MS = 10 * 60_000;

const entries = new Map<string, LiveTrailTimingsEntry>();
const evictionTimers = new Map<string, ReturnType<typeof setTimeout>>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function scheduleEviction(threadId: string): void {
  const existing = evictionTimers.get(threadId);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    entries.delete(threadId);
    evictionTimers.delete(threadId);
    notify();
  }, ENTRY_TTL_MS);
  evictionTimers.set(threadId, timer);
}

export function setLiveRequestStart({
  threadId,
  requestStartedAt,
}: {
  threadId: string;
  requestStartedAt: number;
}): void {
  entries.set(threadId, { requestStartedAt, stepTimings: {} });
  scheduleEviction(threadId);
  notify();
}

function stepTimingsEqual({ a, b }: { a: LiveStepTimingsMap; b: LiveStepTimingsMap }): boolean {
  if (a === b) return true;
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  for (const k of keys) {
    const av = a[k];
    const bv = b[k];
    if (!bv) return false;
    if (
      av.startedAt !== bv.startedAt ||
      av.completedAt !== bv.completedAt ||
      av.gapBeforeMs !== bv.gapBeforeMs
    ) {
      return false;
    }
  }
  return true;
}

export function patchLiveStepTimings({
  threadId,
  patch,
}: {
  threadId: string;
  patch: (current: LiveStepTimingsMap) => LiveStepTimingsMap | undefined;
}): void {
  const entry = entries.get(threadId);
  if (!entry) return;
  const next = patch(entry.stepTimings);
  if (!next) return;
  // Skip notify when the patch produces structurally-equal timings — the
  // transport re-emits identical values for some chunks, and notifying would
  // re-render every `useLiveTrailTimings` consumer for no UI change.
  if (stepTimingsEqual({ a: entry.stepTimings, b: next })) return;
  entries.set(threadId, { ...entry, stepTimings: next });
  scheduleEviction(threadId);
  notify();
}

export function setLiveFinished({
  threadId,
  finishedAt,
}: {
  threadId: string;
  finishedAt: number;
}): void {
  const entry = entries.get(threadId);
  if (!entry) return;
  if (entry.finishedAt === finishedAt) return;
  entries.set(threadId, { ...entry, finishedAt });
  scheduleEviction(threadId);
  notify();
}

export function getLiveTrailTimings(
  threadId: string | undefined,
): LiveTrailTimingsEntry | undefined {
  if (!threadId) return undefined;
  return entries.get(threadId);
}

/** Test-only — wipe all entries + timers between tests. */
export function _resetLiveTrailTimingsForTesting(): void {
  for (const timer of evictionTimers.values()) clearTimeout(timer);
  evictionTimers.clear();
  entries.clear();
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useLiveTrailTimings(
  threadId: string | undefined,
): LiveTrailTimingsEntry | undefined {
  return useSyncExternalStore(
    subscribe,
    () => getLiveTrailTimings(threadId),
    () => undefined,
  );
}
